import classNames from "classnames";
import {
  ReactNode,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { IconBaseProps, IconType } from "react-icons";
import { IoIosArrowDown } from "react-icons/io";
import { Collapsible } from "../Collapsible";
import { CollapsibleProps } from "../Collapsible/Collapsible";
import s from "./CollapsibleContainer.module.scss";

export type CollapsibleContainerRef = {
  open: () => void;
  container: HTMLDivElement | null;
};

export type OnToggleCollapsibleContainerArgs = {
  isOpen: boolean;
};

export type CollapsibleContainerProps = {
  arrowProps?: IconBaseProps;
  collapsibleProps?: Partial<CollapsibleProps>;
  disableBorderBottom?: boolean;
  disableBorderTop?: boolean;
  Icon?: IconType;
  label: string;
  summaryProps?: React.HTMLAttributes<HTMLDivElement>;
  initialIsOpen?: boolean;
  labelRenderer?: (label: string) => ReactNode;
  onToggle?: (args: OnToggleCollapsibleContainerArgs) => unknown;
};
const CollapsibleContainer = forwardRef<
  CollapsibleContainerRef,
  React.PropsWithChildren<CollapsibleContainerProps>
>(
  (
    {
      arrowProps,
      children,
      collapsibleProps,
      disableBorderBottom,
      disableBorderTop,
      Icon,
      label,
      summaryProps,
      initialIsOpen,
      labelRenderer = (label) => <h4>{label}</h4>,
      onToggle,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(!!initialIsOpen);

    const toggleIsOpen = useCallback(
      () =>
        setIsOpen((prevIsOpen) => {
          const isOpen = !prevIsOpen;

          onToggle?.({ isOpen });

          return isOpen;
        }),
      [setIsOpen]
    );

    const divRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      open: () => setIsOpen(true),
      container: divRef.current,
    }));

    return (
      <div
        className={classNames(s.CollapsibleContainer, {
          [s.DisableBorderTop]: disableBorderTop,
          [s.DisableBorderBottom]: disableBorderBottom,
        })}
        ref={divRef}
      >
        <div onClick={toggleIsOpen} className={s.Summary} {...summaryProps}>
          {Icon && <Icon size={24} color="#D81B79" />}
          {labelRenderer(label)}
          <IoIosArrowDown
            className={classNames(s.Arrow, {
              [s.Active]: isOpen,
            })}
            {...arrowProps}
          />
        </div>
        <Collapsible withDynamicDuration isOpen={isOpen} {...collapsibleProps}>
          {children}
        </Collapsible>
      </div>
    );
  }
);

CollapsibleContainer.displayName = "CollapsibleContainer";
export default CollapsibleContainer;
